import { ItemChoiceType, ItemListType, ItemValidationType } from '../types/common';

/**
 * 「ベースプロンプト作成」ボタンで、カスタムプロンプトの下書きを組み立てる（運営向け）。
 *
 * 実際のOCRで使われるのは Functions 側の同名関数
 * （functions/src/utils/common/generatePromptFromItemList.ts）。
 * こちらはブラウザ内で即座に下書きを出すための実装で、実行環境が違うためコードは共有できない。
 * 下書きが本番と食い違うと、それを元に手直ししたプロンプトの指示が意図せず抜けるため、
 * **出力内容は本番側と一致させること**（#3075 で制限指示文の欠落を修正）。
 */

/**
 * 項目バリデーション制約（#2815）から、プロンプトに追記する指示文を組み立てる。
 * Functions 側の buildValidationInstruction と同じ文面にすること。
 */
const buildValidationInstruction = (validation: ItemValidationType): string => {
  const conditions: string[] = [];

  if (validation.required) {
    conditions.push(
      '必須項目のため、記載があれば必ず抽出してください（記載がなければ空のままにしてください）',
    );
  }

  const { minLength, maxLength } = validation;
  if (minLength !== undefined && maxLength !== undefined) {
    conditions.push(`${minLength}文字以上${maxLength}文字以内で抽出してください`);
  } else if (minLength !== undefined) {
    conditions.push(`${minLength}文字以上で抽出してください`);
  } else if (maxLength !== undefined) {
    conditions.push(`${maxLength}文字以内で抽出してください`);
  }

  const { minValue, maxValue } = validation;
  if (minValue !== undefined && maxValue !== undefined) {
    conditions.push(`値は${minValue}以上${maxValue}以下の範囲で抽出してください`);
  } else if (minValue !== undefined) {
    conditions.push(`値は${minValue}以上で抽出してください`);
  } else if (maxValue !== undefined) {
    conditions.push(`値は${maxValue}以下で抽出してください`);
  }

  return conditions.join('。');
};

/**
 * 選択肢型（#3075）の指示文を組み立てる。
 * Functions 側の getChoiceInstruction と同じ文面にすること。
 */
const buildChoiceInstruction = (choices: ItemChoiceType[] | undefined): string => {
  const labels = (choices ?? []).map(choice => choice.label.trim()).filter(label => label !== '');
  if (labels.length === 0) return '';

  return [
    `次の選択肢の中から1つだけ選び、選んだ選択肢の文言をそのまま一字一句変えずに記入してください。`,
    `選択肢: ${labels.join(' / ')}`,
    '選択肢を補ったり言い換えたりしないでください。どれにも該当しない場合は空文字を返してください。',
  ].join('\n  ');
};

/**
 * データ型に応じた日本語指示文を取得する関数。
 * Functions 側の getTypeInstruction と同じ分岐にすること。
 */
const getTypeInstruction = (type?: string, outputFormat?: string): string => {
  switch (type) {
    case 'number':
      return '数値で（コンマを含めず）';
    case 'date':
      return outputFormat ? `日付形式(${outputFormat})で` : '日付形式で';
    case 'array':
      return '配列形式で';
    // 選択肢型（#3075）は候補一覧を別行で示すため、ここでは型の説明を付けない
    case 'radio':
    case 'select':
      return '';
    case 'string':
    default:
      return '文字列で';
  }
};

/**
 * itemListから動的にGeminiプロンプトを生成する関数
 * GEMINI_FORMAT_DATA_PROMPT_FOR_DEMOの出力項目詳細リストを作成する関数
 */
export const generatePromptFromItemList = (
  itemList: ItemListType[],
  documentName?: string,
): string => {
  const docName = documentName || '書類';
  let prompt = '';

  itemList.forEach(item => {
    // 親項目の値が有効な場合のみ処理
    if (item.parentValue && item.parentValue.trim() !== '') {
      // 親項目のセクションを追加
      prompt += `### ${item.parentValue}:\n`;

      // 子項目がある場合
      if (item.children && item.children.length > 0) {
        // 有効な子項目をフィルタリング
        const validChildren = item.children.filter(
          child => child.childValue && child.childValue.trim() !== '',
        );

        if (validChildren.length > 0) {
          // 親項目（array型）自体に取得ルールがある場合は先に出力
          if (item.extractionRule && item.extractionRule.trim() !== '') {
            prompt += `${item.extractionRule}\n`;
          }
          prompt += `${docName}の${item.parentValue}に含まれる以下の各項目を正確に抽出してください：\n`;
          validChildren.forEach(child => {
            const typeInstruction = getTypeInstruction(child.type, child.outputFormat);
            prompt += `- ${child.childValue}: ${item.parentValue}の${child.childValue}を${typeInstruction}記入してください。\n`;
            // 選択肢型（#3075）は候補一覧を提示する。明細の列でも使える
            const childChoiceInstruction = buildChoiceInstruction(child.choices);
            if (childChoiceInstruction !== '') {
              prompt += `  ${childChoiceInstruction}\n`;
            }
            if (child.extractionRule && child.extractionRule.trim() !== '') {
              prompt += `  ${child.extractionRule}\n`;
            }
            const childValidation = buildValidationInstruction(child);
            if (childValidation !== '') {
              prompt += `  ${childValidation}。\n`;
            }
          });
        } else {
          // 子項目が無効な場合は親項目のみ
          const typeInstruction = getTypeInstruction(item.type, item.outputFormat);
          prompt += `${docName}の${item.parentValue}を${typeInstruction}記入してください。\n`;
          if (item.extractionRule && item.extractionRule.trim() !== '') {
            prompt += `${item.extractionRule}\n`;
          }
          const itemValidation = buildValidationInstruction(item);
          if (itemValidation !== '') {
            prompt += `${itemValidation}。\n`;
          }
        }
      } else {
        // 子項目がない場合は親項目のみ
        const typeInstruction = getTypeInstruction(item.type, item.outputFormat);
        prompt += `${docName}の${item.parentValue}を${typeInstruction}記入してください。\n`;
        // 選択肢型（#3075）は候補一覧を提示する
        const choiceInstruction = buildChoiceInstruction(item.choices);
        if (choiceInstruction !== '') {
          prompt += `  ${choiceInstruction}\n`;
        }
        if (item.extractionRule && item.extractionRule.trim() !== '') {
          prompt += `${item.extractionRule}\n`;
        }
        const itemValidation = buildValidationInstruction(item);
        if (itemValidation !== '') {
          prompt += `${itemValidation}。\n`;
        }
      }

      prompt += '\n'; // セクション間の改行
    }
  });

  return prompt;
};
